import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import config from "../../config";
import "./FeedbackPrompt.css";

const FeedbackPrompt = ({ trigger, onDone }) => {
    const dispatch = useDispatch();
    const [campaignData, setCampaignData] = useState(null);
    const [loading, setLoading] = useState(true);

    // phases: "ask" | "questions" | "rate"
    const [phase, setPhase] = useState("ask");
    const [qIndex, setQIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [hoveredStar, setHoveredStar] = useState(0);
    const [selectedStar, setSelectedStar] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios
            .get(`${config.apiUrl}/api/v1/me/feedback/campaign/${trigger}`, { withCredentials: true })
            .then(({ data }) => {
                if (!data.found || !data.eligible) {
                    // No active campaign or user hasn't used the feature enough — dismiss silently
                    clearPrompt();
                } else {
                    setCampaignData(data);
                    setLoading(false);
                }
            })
            .catch(() => {
                // On error, don't interrupt the user — just dismiss
                clearPrompt();
            });
    }, [trigger]);

    const clearPrompt = () => {
        dispatch({ type: "CLEAR_FEEDBACK_PROMPT" });
        onDone && onDone();
    };

    const handleDismiss = () => {
        axios.post(`${config.apiUrl}/api/v1/me/feedback/dismiss`, {}, { withCredentials: true }).catch(() => {});
        clearPrompt();
    };

    const handleAnswer = (option) => {
        const q = campaignData.questions[qIndex];
        const newAnswers = { ...answers, [q.id]: option };
        setAnswers(newAnswers);

        if (qIndex < campaignData.questions.length - 1) {
            setQIndex(qIndex + 1);
        } else {
            setPhase("rate");
        }
    };

    const handleSubmit = async () => {
        if (!selectedStar) return;
        setSubmitting(true);
        const answersArray = Object.entries(answers).map(([id, answer]) => ({
            question: campaignData.questions.find((q) => q.id === id)?.text || id,
            answer,
        }));
        try {
            await axios.post(
                `${config.apiUrl}/api/v1/me/feedback`,
                {
                    rating: selectedStar,
                    comment,
                    trigger,
                    answers: answersArray,
                    campaignId: campaignData.campaignId,
                },
                { withCredentials: true }
            );
        } catch (_) {}
        clearPrompt();
    };

    // Show nothing while checking eligibility
    if (loading) return null;
    if (!campaignData) return null;

    const questions = campaignData.questions || [];
    const currentQ = questions[qIndex];
    const progress = questions.length > 0 ? (qIndex / questions.length) * 100 : 0;

    return (
        <div className="fp-overlay" onClick={handleDismiss}>
            <div className="fp-sheet" onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
                <div className="fp-handle" />
                <button className="fp-close" onClick={handleDismiss} aria-label="Close">&times;</button>

                {/* Ask phase */}
                {phase === "ask" && (
                    <>
                        <p className="fp-heading">Enjoying Dmyor?</p>
                        <p className="fp-sub">Takes 30 seconds. Helps us a lot.</p>
                        <button className="fp-btn-yes" onClick={() => questions.length > 0 ? setPhase("questions") : setPhase("rate")}>
                            Sure, I'd love to
                        </button>
                        <button className="fp-btn-later" onClick={handleDismiss}>
                            Maybe later
                        </button>
                    </>
                )}

                {/* Questions phase */}
                {phase === "questions" && currentQ && (
                    <>
                        <div className="fp-progress-wrap">
                            <div className="fp-progress-bar" style={{ width: `${progress}%` }} />
                        </div>
                        <p className="fp-step-count">{qIndex + 1} of {questions.length}</p>
                        <p className="fp-heading fp-q-heading">{currentQ.text}</p>
                        {currentQ.type === "text" ? (
                          <>
                            <textarea
                              className="fp-comment"
                              placeholder="Your answer..."
                              rows={3}
                              value={answers[currentQ.id] || ""}
                              onChange={(e) => setAnswers((prev) => ({ ...prev, [currentQ.id]: e.target.value }))}
                              autoFocus
                            />
                            <button
                              className="fp-btn-yes"
                              onClick={() => handleAnswer(answers[currentQ.id] || "")}
                            >
                              Next
                            </button>
                          </>
                        ) : (
                          <div className="fp-options">
                            {currentQ.options.map((opt) => (
                              <button key={opt} className="fp-option" onClick={() => handleAnswer(opt)}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        )}
                        <button className="fp-btn-later" onClick={handleDismiss}>Skip all</button>
                    </>
                )}

                {/* Rate phase */}
                {phase === "rate" && (
                    <>
                        <div className="fp-progress-wrap">
                            <div className="fp-progress-bar" style={{ width: "100%" }} />
                        </div>
                        <p className="fp-heading">One last thing — how's your experience overall?</p>
                        <div className="fp-stars">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    className={`fp-star ${n <= (hoveredStar || selectedStar) ? "active" : ""}`}
                                    onMouseEnter={() => setHoveredStar(n)}
                                    onMouseLeave={() => setHoveredStar(0)}
                                    onClick={() => setSelectedStar(n)}
                                    aria-label={`${n} star`}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                        <textarea
                            className="fp-comment"
                            placeholder="Anything else to add? (optional)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                        />
                        <button
                            className="fp-btn-yes"
                            disabled={!selectedStar || submitting}
                            onClick={handleSubmit}
                        >
                            {submitting ? "Sending..." : "Submit"}
                        </button>
                        <button className="fp-btn-later" onClick={handleDismiss}>Cancel</button>
                    </>
                )}
            </div>
        </div>
    );
};

export default FeedbackPrompt;
